import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';

interface ProgressOverview {
  assignment_id: number;
  assignment_name: string;
  due_date: string;
  class_name: string;
  total_students: number;
  completed_students: number;
  in_progress_students: number;
  overdue_students: number;
}

interface StudentProgress {
  student_id: number;
  username: string;
  email: string;
  status: string;
  assigned_at: string;
  attempts: Array<{
    attempt_id: number;
    game_id: number;
    score: number;
    accuracy: number;
    correct_matches: number;
    wrong_matches: number;
    time_spent: number;
    completed_at: string;
  }>;
  best_score: number;
  avg_accuracy: number;
  total_attempts: number;
}

const TeacherProgress: React.FC = () => {
  const [overview, setOverview] = useState<ProgressOverview[]>([]);
  const [selectedAssignment, setSelectedAssignment] = useState<ProgressOverview | null>(null);
  const [detailedProgress, setDetailedProgress] = useState<StudentProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'overdue' | 'completed' | 'pending'>('all');

  useEffect(() => {
    loadOverview();
  }, []);

  const loadOverview = async () => {
    try {
      setLoading(true);
      const data = await apiService.getProgressOverview();
      if (data.success) {
        setOverview(data.overview);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load progress overview');
    } finally {
      setLoading(false);
    }
  };

  const loadAssignmentProgress = async (assignmentId: number) => {
    try {
      setLoading(true);
      const data = await apiService.getAssignmentProgress(assignmentId);
      if (data.success) {
        setDetailedProgress(data.progress);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load assignment progress');
    } finally {
      setLoading(false);
    }
  };

  const handleViewAssignment = async (assignment: ProgressOverview) => {
    setSelectedAssignment(assignment);
    await loadAssignmentProgress(assignment.assignment_id);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'No deadline';
    const date = new Date(dateStr);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const isOverdue = (dateStr: string) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  const getFilteredOverview = () => {
    return overview.filter(item => {
      if (filterStatus === 'all') return true;
      if (filterStatus === 'overdue') return item.overdue_students > 0;
      if (filterStatus === 'completed') return item.completed_students === item.total_students;
      if (filterStatus === 'pending') return item.completed_students < item.total_students;
      return true;
    });
  };

  const getGroupedByClass = () => {
    const filtered = getFilteredOverview();
    const grouped: { [className: string]: ProgressOverview[] } = {};

    filtered.forEach(item => {
      if (!grouped[item.class_name]) {
        grouped[item.class_name] = [];
      }
      grouped[item.class_name].push(item);
    });

    return grouped;
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { text: string; className: string }> = {
      completed: { text: '✅ Completed', className: 'status-completed' },
      in_progress: { text: '🔄 In Progress', className: 'status-in-progress' },
      pending: { text: '⏳ Pending', className: 'status-pending' },
    };
    return badges[status] || { text: status, className: '' };
  };

  const formatDuration = (seconds: number) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  if (loading && overview.length === 0) {
    return <div className="teacher-progress">Loading progress...</div>;
  }

  return (
    <div className="teacher-progress">
      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError('')}>✕</button>
        </div>
      )}

      {/* Overview View */}
      {!selectedAssignment && (
        <div className="progress-overview-view">
          <div className="progress-header">
            <h3>📊 Assignment Progress Overview</h3>
            <div className="filter-buttons">
              <button
                className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
                onClick={() => setFilterStatus('all')}
              >
                All
              </button>
              <button
                className={`filter-btn ${filterStatus === 'overdue' ? 'active' : ''}`}
                onClick={() => setFilterStatus('overdue')}
              >
                ⚠️ Overdue
              </button>
              <button
                className={`filter-btn ${filterStatus === 'completed' ? 'active' : ''}`}
                onClick={() => setFilterStatus('completed')}
              >
                ✅ Completed
              </button>
              <button
                className={`filter-btn ${filterStatus === 'pending' ? 'active' : ''}`}
                onClick={() => setFilterStatus('pending')}
              >
                ⏳ Pending
              </button>
            </div>
          </div>

          {getFilteredOverview().length === 0 ? (
            <div className="empty-state">
              <p>No assignments found matching this filter.</p>
            </div>
          ) : (
            <div className="progress-by-class">
              {Object.entries(getGroupedByClass()).map(([className, classItems]) => (
                <div key={className} className="class-section">
                  <h3 className="class-section-header">
                    📚 {className}
                    <span className="class-assignment-count">({classItems.length} assignment{classItems.length !== 1 ? 's' : ''})</span>
                  </h3>
                  <div className="progress-grid">
                    {classItems.map(item => (
                      <div
                        key={item.assignment_id}
                        className={`progress-card ${isOverdue(item.due_date) && item.completed_students < item.total_students ? 'overdue' : ''}`}
                      >
                        <div className="progress-card-header">
                          <h4>{item.assignment_name}</h4>
                        </div>

                        <div className="progress-stats">
                          <div className="stat-item">
                            <span className="stat-label">Total Students</span>
                            <span className="stat-value">{item.total_students}</span>
                          </div>
                          <div className="stat-item">
                            <span className="stat-label">Completed</span>
                            <span className="stat-value completed">{item.completed_students}</span>
                          </div>
                          <div className="stat-item">
                            <span className="stat-label">In Progress</span>
                            <span className="stat-value in-progress">{item.in_progress_students}</span>
                          </div>
                          {item.overdue_students > 0 && (
                            <div className="stat-item">
                              <span className="stat-label">Overdue</span>
                              <span className="stat-value overdue">{item.overdue_students}</span>
                            </div>
                          )}
                        </div>

                        <div className="progress-bar-container">
                          <div
                            className="progress-bar"
                            style={{ width: `${(item.completed_students / item.total_students) * 100}%` }}
                          />
                        </div>

                        <div className={`progress-due-date ${isOverdue(item.due_date) && item.completed_students < item.total_students ? 'overdue' : ''}`}>
                          📅 {formatDate(item.due_date)}
                          {isOverdue(item.due_date) && item.completed_students < item.total_students && ' (OVERDUE)'}
                        </div>

                        <button onClick={() => handleViewAssignment(item)} className="view-details-btn">
                          View Details →
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Detailed Progress View */}
      {selectedAssignment && (
        <div className="progress-detail-view">
          <div className="progress-detail-header">
            <button onClick={() => {
              setSelectedAssignment(null);
              setDetailedProgress([]);
            }} className="back-btn">
              ← Back to Overview
            </button>
            <div>
              <h3>{selectedAssignment.assignment_name}</h3>
              <p className="detail-class-name">📚 {selectedAssignment.class_name}</p>
            </div>
          </div>

          <div className="detail-summary">
            <div className="summary-stat">
              <span className="summary-label">Total Students:</span>
              <span className="summary-value">{selectedAssignment.total_students}</span>
            </div>
            <div className="summary-stat">
              <span className="summary-label">Completed:</span>
              <span className="summary-value">{selectedAssignment.completed_students} ({Math.round((selectedAssignment.completed_students / selectedAssignment.total_students) * 100)}%)</span>
            </div>
            <div className="summary-stat">
              <span className="summary-label">Due Date:</span>
              <span className={`summary-value ${isOverdue(selectedAssignment.due_date) ? 'overdue' : ''}`}>
                {formatDate(selectedAssignment.due_date)}
              </span>
            </div>
          </div>

          <div className="students-progress-section">
            <h4>Student Progress</h4>
            {detailedProgress.length === 0 ? (
              <div className="empty-state">
                <p>No student data available for this assignment.</p>
              </div>
            ) : (
              <div className="students-progress-list">
                {detailedProgress.map(student => {
                  const badge = getStatusBadge(student.status);
                  return (
                    <div key={student.student_id} className="student-progress-card">
                      <div className="student-progress-header">
                        <div>
                          <h5>{student.username}</h5>
                          <small>{student.email}</small>
                        </div>
                        <span className={`status-badge ${badge.className}`}>
                          {badge.text}
                        </span>
                      </div>

                      <div className="student-progress-summary">
                        <div className="summary-item">
                          <strong>Best Score:</strong> {student.best_score || 'N/A'}
                        </div>
                        <div className="summary-item">
                          <strong>Avg Accuracy:</strong> {student.avg_accuracy ? `${Number(student.avg_accuracy).toFixed(1)}%` : 'N/A'}
                        </div>
                        <div className="summary-item">
                          <strong>Total Attempts:</strong> {student.total_attempts}
                        </div>
                      </div>

                      {student.attempts && student.attempts.length > 0 && (
                        <details className="attempts-details">
                          <summary>View All {student.attempts.length} Attempt{student.attempts.length !== 1 ? 's' : ''}</summary>
                          <table className="attempts-table">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Score</th>
                                <th>Accuracy</th>
                                <th>Correct</th>
                                <th>Wrong</th>
                                <th>Time</th>
                                <th>Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {student.attempts.map((attempt, index) => (
                                <tr
                                  key={attempt.attempt_id}
                                  className={attempt.score === student.best_score ? 'best-attempt' : ''}
                                >
                                  <td>{index + 1}</td>
                                  <td>
                                    {attempt.score}
                                    {attempt.score === student.best_score && ' 🏆'}
                                  </td>
                                  <td>{attempt.accuracy ? `${Number(attempt.accuracy).toFixed(1)}%` : 'N/A'}</td>
                                  <td>{attempt.correct_matches}</td>
                                  <td>{attempt.wrong_matches}</td>
                                  <td>{formatDuration(attempt.time_spent)}</td>
                                  <td>{new Date(attempt.completed_at).toLocaleString()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </details>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherProgress;
